import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Canvas, useLoader } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { 
  Phone, Mail, MessageCircle, Youtube, Send, 
  User, Smartphone, BookOpen, ChevronDown, CheckCircle, AlertCircle,
  Instagram, Facebook
} from 'lucide-react';
import * as THREE from 'three';
import emailjs from '@emailjs/browser';
import { injectFonts, T } from '../utils/fonts';
import { trackLead, leadReference } from '../utils/pixel';

/* ── Aurora Background ── */
const Aurora = () => {
  useEffect(() => {
    const id = 'aurora-kf';
    if (document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      @keyframes aurora1{0%{transform:translate(0,0) scale(1);opacity:.55}33%{transform:translate(8%,-12%) scale(1.15);opacity:.7}66%{transform:translate(-5%,8%) scale(.95);opacity:.5}100%{transform:translate(0,0) scale(1);opacity:.55}}
      @keyframes aurora2{0%{transform:translate(0,0) scale(1);opacity:.45}33%{transform:translate(-10%,6%) scale(1.1);opacity:.6}66%{transform:translate(7%,-9%) scale(1.05);opacity:.4}100%{transform:translate(0,0) scale(1);opacity:.45}}
      @keyframes aurora3{0%{transform:translate(0,0) scale(1.05);opacity:.4}50%{transform:translate(6%,10%) scale(.9);opacity:.6}100%{transform:translate(0,0) scale(1.05);opacity:.4}}
      @keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}
      @keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
      @keyframes cardIn{from{opacity:0;transform:translateY(32px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}
      @keyframes borderSlide{0%{background-position:200% 0}100%{background-position:-200% 0}}
    `;
    document.head.appendChild(s);
  }, []);
  return (
    <div style={{position:'absolute',inset:0,overflow:'hidden',pointerEvents:'none',zIndex:0,filter:'blur(80px)'}} aria-hidden="true">
      <div style={{position:'absolute',width:'70vw',height:'70vw',maxWidth:900,maxHeight:900,borderRadius:'50%',background:'radial-gradient(circle,rgba(99,102,241,.55) 0%,rgba(139,92,246,.3) 50%,transparent 70%)',top:'-20%',left:'-10%',animation:'aurora1 14s ease-in-out infinite'}}/>
      <div style={{position:'absolute',width:'65vw',height:'65vw',maxWidth:800,maxHeight:800,borderRadius:'50%',background:'radial-gradient(circle,rgba(6,182,212,.5) 0%,rgba(16,185,129,.25) 50%,transparent 70%)',bottom:'-10%',right:'-10%',animation:'aurora2 18s ease-in-out infinite'}}/>
      <div style={{position:'absolute',width:'55vw',height:'55vw',maxWidth:700,maxHeight:700,borderRadius:'50%',background:'radial-gradient(circle,rgba(245,158,11,.35) 0%,rgba(239,68,68,.2) 50%,transparent 70%)',top:'30%',left:'35%',animation:'aurora3 22s ease-in-out infinite'}}/>
    </div>
  );
};

/* ── SpotlightCard ── */
const SpotlightCard = ({ children, accentColor = T.godot, animDelay = 0 }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x:'50%', y:'50%', visible:false });
  const onMove = (e) => { const r = ref.current.getBoundingClientRect(); setPos({ x:`${e.clientX-r.left}px`, y:`${e.clientY-r.top}px`, visible:true }); };
  const onLeave = () => setPos(p => ({ ...p, visible:false }));
  return (
    // The grid stretches this wrapper to the tallest column, but a block child
    // inside it keeps its own content height — which is why the two panels
    // stopped matching once the form grew. Flex column plus a stretching last
    // child passes the full height through to the card itself.
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className="spotlight-card"
      style={{ position:'relative', animation:`cardIn 0.6s ${animDelay}s both cubic-bezier(0.4,0,0.2,1)` }}>
      <div style={{
        position:'absolute', inset:0, borderRadius:20,
        background:`radial-gradient(320px circle at ${pos.x} ${pos.y}, ${accentColor}28 0%, transparent 65%)`,
        opacity: pos.visible ? 1 : 0, transition:'opacity 0.35s ease',
        pointerEvents:'none', zIndex:3,
      }} />
      {children}
    </div>
  );
};

/* ── 3D Globe ── */
const TexturedSphere = () => {
  const texture = useLoader(THREE.TextureLoader, '/textures/earth.jpg');
  return (
    <mesh castShadow>
      <sphereGeometry args={[2, 64, 64]} />
      <meshStandardMaterial map={texture} />
    </mesh>
  );
};

/* ── Reusable Info Card ── */
const InfoCard = ({ icon, label, children, accent = T.godot }) => (
  <div style={{
    background: `${accent}0a`,
    border: `1px solid ${accent}25`,
    borderRadius: 14,
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    direction: 'rtl',
    fontFamily: T.fontBody,
    transition: 'all 0.3s ease',
  }} className="info-card-hover">
    <div style={{
      width:48, height:48, borderRadius:12,
      background:`linear-gradient(135deg,${accent},${accent}99)`,
      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
      boxShadow:`0 4px 12px ${accent}30`,
    }}>{icon}</div>
    <div style={{ textAlign:'right', minWidth:0 }}>
      <p style={{ fontSize:'0.75rem', color:T.faint, margin:'0 0 4px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</p>
      {children}
    </div>
  </div>
);

const ContactPage = () => {
  useEffect(() => { injectFonts(); }, []);
  
  const formRef = useRef();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [status, setStatus] = useState('idle');
  // One reference per mounted form, shared by the email and the Meta event.
  const reference = useMemo(() => leadReference(), []);
  const [isDesktop, setIsDesktop] = useState(false);

  // Resize listener for responsive layout shifts
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => setIsDesktop(window.innerWidth >= 768);
      handleResize(); // Initial check
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();
    setStatus('loading');

    const form = formRef.current;
    const data = new FormData(form);
    const phone = data.get('user_phone');
    const courseInterest = data.get('course_interest');
    const name = String(data.get('user_name') || '').trim();

    // TODO: Replace these with your actual EmailJS keys
    const serviceID = 'service_j25e6gl';
    const templateID = 'template_1hn9h46';
    const publicKey = 'X4BurVzKo6HJ-wI3-';

    emailjs.sendForm(serviceID, templateID, form, publicKey)
      .then((result) => {
          setStatus('success');
          form.reset();
          // Consent is given by submitting — see the notice under the button.
          trackLead({
            phone,
            firstName: name.split(' ')[0],
            reference,
            contentName: courseInterest || 'General enquiry',
            contentCategory: 'contact',
            leadSource: 'Contact page',
          });
          setTimeout(() => setStatus('idle'), 5000);
      }, (error) => {
          console.error(error.text);
          setStatus('error');
          setTimeout(() => setStatus('idle'), 5000);
      });
  };

  return (
    <div style={styles.page} dir="rtl">
      <Aurora />

      <header style={{ width:'100%', display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', marginBottom:'clamp(32px,6vw,56px)', position:'relative', zIndex:1, animation:'fadeUp 0.7s 0.1s both' }}>
        <p style={{ width:'100%', textAlign:'center', fontSize:'0.85rem', color:T.godot, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', margin:'0 auto 12px', fontFamily: T.fontBody }}>
          דברו איתנו
        </p>
        <h1 style={{
          fontSize:'clamp(2.2rem,8vw,4rem)', margin:'0 0 12px', fontWeight:700, letterSpacing:'-0.02em',
          fontFamily: T.fontDisplay,
          background:`linear-gradient(90deg,${T.text} 20%,#fff 40%,${T.muted} 60%,${T.text} 80%)`,
          backgroundSize:'200% auto', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
          backgroundClip:'text', animation:'shimmer 3.5s linear infinite',
        }}>צור קשר</h1>
        <p style={{ fontSize:'clamp(0.95rem,3vw,1.1rem)', color:T.muted, margin:'0 auto', maxWidth:540, lineHeight:1.6, fontFamily: T.fontBody }}>
          יש לכם שאלה? רוצים להירשם? השאירו פרטים ונחזור אליכם בהקדם האפשרי.
        </p>
      </header>

      <div className="contact-cards-grid" style={styles.contentGrid}>
        
        {/* ── Right Column: Contact Form ── */}
        <SpotlightCard accentColor={T.violet} animDelay={0.1}>
        <div
          style={{
            ...styles.card,
            
            transform: hoveredCard === 'form' ? 'translateY(-4px)' : 'translateY(0)',
            boxShadow: hoveredCard === 'form' ? '0 24px 48px rgba(6,182,212,0.15), 0 0 0 1px rgba(6,182,212,0.3)' : '0 8px 32px rgba(0,0,0,0.3)',
          }}
          onMouseEnter={() => setHoveredCard('form')}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <div style={{ height:3, background:`linear-gradient(90deg,transparent 0%,${T.violet} 30%,${T.godot}80 70%,transparent 100%)`, backgroundSize:'200% 100%', animation:'borderSlide 4s linear infinite', borderRadius:'0 0 3px 3px', marginBottom:24 }} />
          <h2 style={{ fontSize:'clamp(1.3rem,4vw,1.6rem)', color:T.text, fontWeight:700, margin:'0 0 6px', textAlign:'right', fontFamily: T.fontDisplay }}>
            השאירו פרטים
          </h2>
          <p style={{ fontSize:'0.9rem', color:T.faint, marginBottom:24, textAlign:'right' }}>מלאו את הטופס ונחזור אליכם בהקדם.</p>
          
          <form ref={formRef} onSubmit={sendEmail} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            
            <div className="input-group">
              <label style={styles.label}>שם ההורה או האפוטרופוס</label>
              <div className="input-wrapper">
                <User size={18} className="input-icon right-icon" />
                <input type="text" name="user_name" required className="contact-input" placeholder="השם שלכם..." disabled={status === 'loading'} />
              </div>
            </div>

            <div className="input-group">
              <label style={styles.label}>טלפון של ההורה או האפוטרופוס</label>
              <div className="input-wrapper">
                <Smartphone size={18} className="input-icon right-icon" />
                <input type="tel" name="user_phone" required className="contact-input" placeholder="050-0000000" dir="ltr" disabled={status === 'loading'} />
              </div>
            </div>

            <input type="hidden" name="lead_reference" value={reference} readOnly />

            <div className="input-group">
              <label style={styles.label}>קורס מבוקש</label>
              <div className="input-wrapper">
                <BookOpen size={18} className="input-icon right-icon" />
                <select name="course_interest" required className="contact-input select-input" defaultValue="" disabled={status === 'loading'}>
                  <option value="" disabled>בחרו קורס...</option>
                  <option value="פיתוח משחקים (Godot)">פיתוח משחקים (Godot)</option>
                  <option value="תכנות פייתון (Python)">תכנות פייתון (Python)</option>
                  <option value="תכנות במיינקראפט">תכנות במיינקראפט</option>
                  <option value="פיתוח אתרים 3D">פיתוח אתרים 3D</option>
                  <option value="אחר / מתלבט">אחר / מתלבט</option>
                </select>
                <ChevronDown size={18} className="input-icon left-icon" />
              </div>
            </div>

            <button 
              type="submit" 
              className={`submit-btn ${status === 'loading' ? 'loading' : ''}`}
              disabled={status === 'loading' || status === 'success'}
              style={{ marginTop: '56px' }}
            >
              {status === 'loading' ? 'שולח...' : status === 'success' ? <><CheckCircle size={18} /> נשלח בהצלחה!</> : <><Send size={18} /> שליחת פרטים</>}
            </button>

            <p className="form-notice">
              בשליחת הטופס אני מאשר/ת שהפרטים ישמשו ליצירת קשר וכן להעברה מוצפנת
              ל‑Meta למדידת ביצועי המודעות, בהתאם{' '}
              <Link to="/privacy" className="gg-link">למדיניות הפרטיות</Link>.
            </p>

            {status === 'error' && (
              <p style={{ color: T.red, fontSize: '0.85rem', textAlign: 'center', margin: '4px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <AlertCircle size={14} /> אירעה שגיאה. אנא נסו שוב או צרו קשר בווצאפ.
              </p>
            )}
          </form>
        </div>
        </SpotlightCard>

        {/* ── Left Column: Direct Info & Socials ── */}
        <SpotlightCard accentColor={T.godot} animDelay={0.23}>
        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: isDesktop ? 0 : 'clamp(24px, 4vw, 36px)',
            ...(isDesktop ? {
              ...styles.card,
              transform: hoveredCard === 'leftCol' ? 'translateY(-4px)' : 'translateY(0)',
              boxShadow: hoveredCard === 'leftCol' ? '0 24px 48px rgba(99,102,241,0.15), 0 0 0 1px rgba(99,102,241,0.3)' : '0 8px 32px rgba(0,0,0,0.3)',
            } : {})
          }}
          onMouseEnter={isDesktop ? () => setHoveredCard('leftCol') : undefined}
          onMouseLeave={isDesktop ? () => setHoveredCard(null) : undefined}
        >
          
          {/* Section 1: Direct Contact Info */}
          <div
            style={{
              ...(isDesktop ? { 
                display: 'flex', 
                flexDirection: 'column' 
              } : {
                ...styles.card,
                transform: hoveredCard === 'info' ? 'translateY(-4px)' : 'translateY(0)',
                boxShadow: hoveredCard === 'info' ? '0 24px 48px rgba(99,102,241,0.15), 0 0 0 1px rgba(99,102,241,0.3)' : '0 8px 32px rgba(0,0,0,0.3)',
                display: 'flex',
                flexDirection: 'column'
              })
            }}
            onMouseEnter={!isDesktop ? () => setHoveredCard('info') : undefined}
            onMouseLeave={!isDesktop ? () => setHoveredCard(null) : undefined}
          >
            <div style={{ height:3, background:`linear-gradient(90deg,transparent 0%,${T.godot} 30%,#8b5cf680 70%,transparent 100%)`, backgroundSize:'200% 100%', animation:'borderSlide 4s linear infinite', borderRadius:'0 0 3px 3px', marginBottom:24 }} />
            <h2 style={{ fontSize:'clamp(1.3rem,4vw,1.6rem)', color:T.text, fontWeight:700, margin:'0 0 6px', textAlign:'right', fontFamily: T.fontDisplay }}>
              יצירת קשר ישירה
            </h2>
            <p style={{ fontSize:'0.9rem', color:T.faint, marginBottom:24, textAlign:'right' }}>מעדיפים לדבר איתנו ישירות? אנחנו זמינים.</p>

            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <InfoCard icon={<Phone size={20} color="white" />} label="טלפון" accent={T.godot}>
                <a href="tel:054-944-7314" style={styles.link}>054-944-7314</a>
              </InfoCard>
              <InfoCard icon={<Mail size={20} color="white" />} label="אימייל" accent={T.violet}>
                <a href="mailto:gogamedev3d@gmail.com" style={styles.link}>GoGameDev3D@gmail.com</a>
              </InfoCard>
              <InfoCard icon={<MessageCircle size={20} color="white" />} label="WhatsApp" accent="#10b981">
                <a href="https://wa.me/972549447314" target="_blank" rel="noopener noreferrer" style={styles.link}>שלח הודעה בווצאפ</a>
              </InfoCard>
            </div>
          </div>

          {/* Section 2: Socials & Globe */}
          <div
            style={{
              ...(isDesktop ? {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '20px',
                marginTop: '32px',
                paddingTop: '32px',
                borderTop: '1px solid rgba(255,255,255,0.08)'
              } : {
                ...styles.card,
                transform: hoveredCard === 'socials' ? 'translateY(-4px)' : 'translateY(0)',
                boxShadow: hoveredCard === 'socials' ? '0 24px 48px rgba(239,68,68,0.15), 0 0 0 1px rgba(239,68,68,0.3)' : '0 8px 32px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '20px'
              })
            }}
            onMouseEnter={!isDesktop ? () => setHoveredCard('socials') : undefined}
            onMouseLeave={!isDesktop ? () => setHoveredCard(null) : undefined}
          >
            {/* Social Links */}
            <div>

              <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                {[
                  { 
                    href: 'https://www.youtube.com/@GoGameDevAcademy', 
                    label: 'YouTube', 
                    icon: Youtube, 
                    bg: `linear-gradient(135deg,${T.red},#dc2626)` 
                  },
                  { 
                    href: 'https://www.instagram.com/gogamedev3d/', 
                    label: 'Instagram', 
                    icon: Instagram, 
                    bg: 'linear-gradient(135deg,#f58529,#dd2a7b,#8134af)' 
                  },
                  { 
                    href: 'https://www.facebook.com/people/Gogamedev-לימודי-תכנות-ופיתוח/61574502521822/', 
                    label: 'Facebook', 
                    icon: Facebook, 
                    bg: 'linear-gradient(135deg,#1877f2,#0c5ebd)' 
                  },
                ].map((s, i) => {
                  const IconTag = s.icon;
                  return (
                    <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="social-btn"
                      style={{ width:48, height:48, borderRadius:14, background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none' }}>
                      <IconTag size={22} color="white" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* 3D Globe - Hide on Desktop */}
            {!isDesktop && (
              <div style={{ width: 60, height: 90, flexShrink: 0, margin: '0 auto' }}>
                <Canvas camera={{ position: [0, 0, 4.5] }}>
                  <ambientLight intensity={1.0} />
                  <directionalLight position={[5,10,5]} intensity={1.2} />
                  <TexturedSphere />
                  <OrbitControls enableZoom={false} autoRotate autoRotateSpeed={2.5} enablePan={false} />
                </Canvas>
              </div>
            )}
          </div>

        </div>
        </SpotlightCard>

      </div>

      <footer style={{ width:'100%', boxSizing:'border-box', display:'flex', justifyContent:'center', alignItems:'center', textAlign:'center', padding:'40px 20px 20px', position:'relative', zIndex:1 }}>
        <p style={{ width:'100%', textAlign:'center', color:T.muted, fontSize:'0.88rem', margin:'0 auto', fontFamily: T.fontBody }}>
          &copy; {new Date().getFullYear()} GoGameDev3D - כל הזכויות שמורות
        </p>
      </footer>

      <style>{`
        /* Form Input Styling */
        .input-group { position: relative; }
        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .input-icon {
          position: absolute;
          color: ${T.faint};
          transition: color 0.3s ease;
          pointer-events: none;
        }
        .right-icon { right: 14px; }
        .left-icon { left: 14px; }

        .contact-input {
          width: 100%;
          padding: 14px 44px 14px 44px;
          background: ${T.panelUp};
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          color: ${T.text};
          font-family: inherit;
          font-size: 0.95rem;
          outline: none;
          transition: all 0.3s ease;
          box-sizing: border-box;
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
        }
        .contact-input::placeholder { color: ${T.muted}; }
        .contact-input:focus {
          border-color: ${T.godot};
          background: ${T.panel};
          box-shadow: 0 0 0 3px rgba(6,182,212,0.15), inset 0 2px 4px rgba(0,0,0,0.1);
        }
        .contact-input:focus ~ .input-icon, 
        .input-wrapper:focus-within .input-icon {
          color: ${T.godot};
        }
        .contact-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        /* Select Dropdown Reset - Strongly enforcing no default browser arrows */
        .select-input {
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: none !important;
          cursor: pointer;
        }
        select option {
          background-color: ${T.panel};
          color: ${T.text};
          padding: 10px;
        }

        /* Buttons */
        .form-notice {
          margin: 10px 0 0;
          font-size: 0.78rem;
          line-height: 1.6;
          color: ${T.muted};
          text-align: right;
        }

        .submit-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 16px 24px;
          background: linear-gradient(135deg, ${T.violet}, ${T.godot});
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 700;
          font-size: 1.05rem;
          font-family: inherit;
          cursor: pointer;
          box-shadow: 0 6px 20px rgba(6,182,212,0.25);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .submit-btn:not(:disabled):hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(6,182,212,0.4);
          background: linear-gradient(135deg, #08c5e6, #4f91ff);
        }
        .submit-btn:not(:disabled):active { transform: translateY(1px); }
        .submit-btn:disabled {
          background: ${T.muted};
          box-shadow: none;
          cursor: not-allowed;
        }
        .submit-btn.loading {
          animation: pulseBtn 1.5s infinite alternate;
        }
        
        @keyframes pulseBtn {
          0% { opacity: 0.8; }
          100% { opacity: 1; }
        }
        
        .social-btn {
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          transition: all 0.3s ease;
        }
        .social-btn:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 8px 20px rgba(0,0,0,0.4);
        }

        .info-card-hover:hover {
          background: rgba(99,102,241,0.08) !important;
          border-color: rgba(99,102,241,0.3) !important;
        }

        /* The grid stretches each column; this passes that height through the
           wrapper to the card, so the two panels stay level as one grows. */
        .spotlight-card { display: flex; flex-direction: column; height: 100%; }
        .spotlight-card > :last-child { flex: 1; }

        @media (max-width: 767px) {
          .contact-cards-grid {
            grid-template-columns: minmax(0, 640px) !important;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    background: T.bg,
    minHeight: '100vh',
    padding: 'clamp(20px,5vw,40px)',
    paddingTop: 'clamp(80px,14vw,120px)',
    fontFamily: T.fontBody,
    position: 'relative',
    overflow: 'hidden',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 'clamp(24px, 4vw, 36px)',
    width: '100%',
    maxWidth: 1000,
    margin: '0 auto',
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'stretch',
    position: 'relative', zIndex: 1,
  },
  card: {
    background: T.panel,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 20,
    padding: 'clamp(24px, 4vw, 36px)',
    transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1), box-shadow 0.4s ease',
    fontFamily: T.fontBody,
  },
  link: {
    fontSize: 'clamp(0.9rem,3vw,1.05rem)',
    color: T.text, fontWeight: 700, textDecoration: 'none',
    wordBreak: 'break-all',
  },
  label: {
    display: 'block',
    fontSize: '0.82rem',
    color: T.muted,
    marginBottom: '6px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
};

export default ContactPage;
